import { z } from 'zod';
/**
 * Планировка хранится одним JSON-документом (floor_plans.data): стены и
 * комнаты правятся вместе и сохраняются целиком, отдельные таблицы на каждую
 * вершину дали бы только лишние джойны. Все координаты — целые сантиметры
 * в системе «ось Y вниз», как в SVG.
 */
export const FLOOR_PLAN_MAX_PER_PROJECT = 20;
export const FLOOR_PLAN_MAX_WALLS = 500;
export const FLOOR_PLAN_MAX_ROOMS = 100;
export const FLOOR_PLAN_MAX_ROOM_POINTS = 100;
/** Толщина стены по умолчанию — типовая межкомнатная перегородка. */
export const FLOOR_PLAN_DEFAULT_WALL_THICKNESS_CM = 12;
/** Высота потолка по умолчанию — типовая для жилья; нужна, чтобы считать площадь стен под плитку и краску. */
export const FLOOR_PLAN_DEFAULT_CEILING_CM = 270;
export const FLOOR_PLAN_MIN_CEILING_CM = 200;
export const FLOOR_PLAN_MAX_CEILING_CM = 600;
const ceiling = z.number().int().min(FLOOR_PLAN_MIN_CEILING_CM).max(FLOOR_PLAN_MAX_CEILING_CM);
/** ±100 м от начала координат — с запасом для любого дома. */
const coord = z.number().int().min(-10_000).max(10_000);
export const floorPlanPointSchema = z.object({ x: coord, y: coord });
export const FLOOR_PLAN_OPENING_MIN_WIDTH_CM = 30;
export const FLOOR_PLAN_OPENING_MAX_WIDTH_CM = 500;
export const FLOOR_PLAN_DEFAULT_DOOR_WIDTH_CM = 80;
export const FLOOR_PLAN_DEFAULT_WINDOW_WIDTH_CM = 120;
export const FLOOR_PLAN_DEFAULT_PASSAGE_WIDTH_CM = 90;
/** Ограждение балкона — тонкая стенка-парапет, не несущая стена. */
export const FLOOR_PLAN_RAILING_THICKNESS_CM = 8;
/** Граница зоны хранится с минимальной толщиной, в расчётах её толщина — 0. */
export const FLOOR_PLAN_DIVIDER_THICKNESS_CM = 2;
export const FLOOR_PLAN_DEFAULT_BALCONY_WIDTH_CM = 300;
export const FLOOR_PLAN_DEFAULT_BALCONY_DEPTH_CM = 120;
/** PASSAGE — проход без двери: арка, широкий проём между кухней и гостиной. */
export const floorPlanOpeningKindEnum = z.enum(['DOOR', 'WINDOW', 'PASSAGE']);
/**
 * Проём (дверь/окно) живёт внутри своей стены: положение задано от её начала
 * (a), поэтому при перемещении/растягивании стены проём едет вместе с ней.
 * Если стену укоротили так, что проём не влезает, он прижимается к краю
 * при отрисовке — данные не портятся.
 */
export const floorPlanOpeningSchema = z.object({
    id: z.string().min(1).max(40),
    kind: floorPlanOpeningKindEnum,
    /** Расстояние от начала стены (a) до центра проёма, см. */
    offset: z.number().int().min(0).max(20_000),
    width: z.number().int().min(FLOOR_PLAN_OPENING_MIN_WIDTH_CM).max(FLOOR_PLAN_OPENING_MAX_WIDTH_CM),
    /** Дверь: петли у конца проёма, ближнего к b (по умолчанию — к a). */
    hingeAtEnd: z.boolean().optional(),
    /** Дверь: открывается на другую сторону стены. */
    flipSide: z.boolean().optional(),
});
export const floorPlanWallSchema = z.object({
    id: z.string().min(1).max(40),
    a: floorPlanPointSchema,
    b: floorPlanPointSchema,
    thickness: z.number().int().min(2).max(100),
    openings: z.array(floorPlanOpeningSchema).max(20).optional(),
    /** Ограждение балкона: рисуется тоньше и не делает контур «комнатой», а делает его балконом. */
    railing: z.boolean().optional(),
    /**
     * Граница зоны, а не стена: делит открытое пространство (коридор → кухня-гостиная)
     * на комнаты, но толщины не имеет — площадь не съедает, в 3D и для мебели её нет.
     */
    divider: z.boolean().optional(),
});
/**
 * Отделка комнаты — для 3D и подсчёта материалов. Без значения угадывается по
 * названию (санузел → плитка) — floorPlanRoomFloorFinish / floorPlanRoomWallFinish.
 */
export const floorPlanFloorFinishEnum = z.enum(['WOOD', 'LAMINATE', 'TILE', 'STONE', 'CARPET', 'CONCRETE']);
export const floorPlanWallFinishEnum = z.enum(['PAINT', 'WALLPAPER', 'TILE', 'PLASTER', 'PANELS']);
export const floorPlanRoomSchema = z.object({
    id: z.string().min(1).max(40),
    name: z.string().max(60),
    points: z.array(floorPlanPointSchema).min(3).max(FLOOR_PLAN_MAX_ROOM_POINTS),
    /** Балкон/терраса: считается отдельно и не входит в общую площадь. Выводится из стен. */
    outdoor: z.boolean().optional(),
    /** Высота потолка этой комнаты, см; без значения — общая по плану (data.ceilingHeight). */
    ceilingHeight: ceiling.optional(),
    floorFinish: floorPlanFloorFinishEnum.optional(),
    wallFinish: floorPlanWallFinishEnum.optional(),
});
/**
 * Фото (скан, снимок готового чертежа) под обводку: лежит в мировых
 * координатах как обычный прямоугольник, ссылается на Attachment
 * (kind FLOOR_PLAN_BG) — сам файл и подписанная ссылка не хранятся здесь.
 */
export const floorPlanBackgroundSchema = z.object({
    attachmentId: z.string(),
    x: coord,
    y: coord,
    width: z.number().int().min(10).max(20_000),
    height: z.number().int().min(10).max(20_000),
    opacity: z.number().min(0.1).max(1),
    locked: z.boolean(),
});
/**
 * Слои поверх плана (стены/проёмы/комнаты — это базовый «План», он не слой).
 * Новый слой = новое значение здесь + записи в каталоге (floor-plan-catalog.ts).
 */
export const floorPlanLayerEnum = z.enum(['FURNITURE', 'ELECTRICAL', 'PLUMBING']);
export const FLOOR_PLAN_MAX_ITEMS = 500;
/**
 * Предмет слоя: тип из каталога (диван, кровать, …), центр, размер «по
 * габаритам» и поворот. Тип — свободная строка: незнакомый клиенту тип
 * рисуется простым прямоугольником, а не ломает документ.
 */
export const floorPlanItemSchema = z.object({
    id: z.string().min(1).max(40),
    layer: floorPlanLayerEnum,
    type: z.string().min(1).max(40),
    x: coord,
    y: coord,
    width: z.number().int().min(5).max(2_000),
    depth: z.number().int().min(5).max(2_000),
    /** Градусы по часовой стрелке, 0–359. */
    rotation: z.number().int().min(0).max(359),
    /** Высота установки от чистового пола, см (розетки, выключатели, выводы). */
    elevation: z.number().int().min(0).max(500).optional(),
});
export const floorPlanDataSchema = z.object({
    version: z.literal(1),
    walls: z.array(floorPlanWallSchema).max(FLOOR_PLAN_MAX_WALLS),
    rooms: z.array(floorPlanRoomSchema).max(FLOOR_PLAN_MAX_ROOMS),
    background: floorPlanBackgroundSchema.nullable().optional(),
    items: z.array(floorPlanItemSchema).max(FLOOR_PLAN_MAX_ITEMS).optional(),
    /** Общая высота потолков, см (по умолчанию FLOOR_PLAN_DEFAULT_CEILING_CM). */
    ceilingHeight: ceiling.optional(),
});
export const createFloorPlanSchema = z.object({
    name: z.string().trim().min(1).max(100),
    data: floorPlanDataSchema.optional(),
});
export const updateFloorPlanSchema = z
    .object({
    name: z.string().trim().min(1).max(100),
    data: floorPlanDataSchema,
})
    .partial();
export const floorPlanSchema = z.object({
    id: z.string(),
    projectId: z.string(),
    name: z.string(),
    data: floorPlanDataSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Настоящие стены — без границ зон: для 3D, мебели, линейки. */
export function floorPlanSolidWalls(walls) {
    return walls.some((w) => w.divider) ? walls.filter((w) => !w.divider) : walls;
}
export function emptyFloorPlanData() {
    return { version: 1, walls: [], rooms: [] };
}
/** Площадь многоугольника в м² (формула шнурования; точки в см). */
export function floorPlanPolygonAreaM2(points) {
    let sum = 0;
    for (let i = 0; i < points.length; i++) {
        const p = points[i];
        const q = points[(i + 1) % points.length];
        sum += p.x * q.y - q.x * p.y;
    }
    return Math.abs(sum) / 2 / 10_000;
}
/** Суммарная площадь комнат в м² — для карточки в списке. Балконы не входят: считаются отдельно. */
export function floorPlanTotalAreaM2(data) {
    return data.rooms.reduce((acc, room) => (room.outdoor ? acc : acc + floorPlanPolygonAreaM2(room.points)), 0);
}
/** Площадь балконов и террас в м² — отдельной строкой, как в документах на квартиру. */
export function floorPlanOutdoorAreaM2(data) {
    return data.rooms.reduce((acc, room) => (room.outdoor ? acc + floorPlanPolygonAreaM2(room.points) : acc), 0);
}
/** Число жилых комнат без балконов — для подписей «2 комнаты». */
export function floorPlanIndoorRoomCount(data) {
    return data.rooms.filter((room) => !room.outdoor).length;
}
/** Высота потолка комнаты, см: своя, иначе общая по плану, иначе типовая. */
export function floorPlanRoomCeilingCm(data, room) {
    return room.ceilingHeight ?? data.ceilingHeight ?? FLOOR_PLAN_DEFAULT_CEILING_CM;
}
//# sourceMappingURL=floor-plan.js.map